<template>
    <div class="account-page">
        <b-container class="account-container">
            <el-container>
                <el-aside width="100">
                    <el-menu
                        :default-openeds="activePane"
                        class="profile-aside-menu"
                        :unique-opened="true"
                        :collapse="isCollapse"
                    >
                        <el-menu-item index="1" @click="collapseSide">
                            <i class="icon-account-toggle"></i>
                        </el-menu-item>

                        <el-menu-item index="2">
                            <span slot="title">
                                <span :class="`user-avatar ${user.tier}`">
                                    <span class="user-img">
                                        <img :src="user.defaultAvatar" alt="Default User Avatar" />
                                    </span>
                                    <span class="user-info">
                                        <span class="user-name">{{user.name}}</span>
                                        <span class="user-class">{{user.tierLabel}}</span>
                                    </span>
                                </span>
                                <a
                                    class="user-benefits"
                                    href="https://propertypassbook.com/optimize-your-property-portfolio/#benefits"
                                >{{$t('profile.benefits')}}</a>
                            </span>
                        </el-menu-item>

                        <el-menu-item index="8">
                            <a class="aside-link" @click="addProperty();handleOpen()">
                                <i class="icon-account icon-addProp"></i>
                                <span slot="title">{{$t('addProperty.addProp')}}</span>
                            </a>
                        </el-menu-item>

                        <el-menu-item
                            index="3"
                            :class="{'is-active': switchContent == 'portfolio'}"
                        >
                            <a class="aside-link" @click="changeUrl('portfolio');handleOpen()">
                                <i class="icon-account icon-portfolio"></i>
                                <span slot="title">{{$t('profile.portfolio')}}</span>
                            </a>
                        </el-menu-item>

                        <el-submenu index="4" :class="{'is-active': switchContent == 'property'}">
                            <template slot="title">
                                <a class="aside-link">
                                    <i class="icon-account icon-property"></i>
                                    <span slot="title">{{$t('profile.property')}}</span>
                                </a>
                            </template>
                            <el-menu-item-group>
                                <el-menu-item
                                    v-for="(item, index) in portfolioResp.properties"
                                    :key="index"
                                    class="property-list"
                                    :index="`2-${index}`"
                                >
                                    <a @click="linkToPID(item);handleOpen()">{{item.name}}</a>
                                </el-menu-item>
                            </el-menu-item-group>
                        </el-submenu>

                        <el-submenu index="5" :class="{'is-active': switchContent == 'manage'}">
                            <template slot="title">
                                <a class="aside-link">
                                    <i class="icon-account icon-manage"></i>
                                    <span slot="title">{{$t('profile.manage')}}</span>
                                </a>
                            </template>

                            <el-menu-item-group>
                                <el-menu-item
                                    v-for="(item, index) in portfolioResp.properties"
                                    :key="index"
                                    class="property-list"
                                    :index="`2-${index}`"
                                >
                                    <a @click="linkToManage(item);handleOpen()">{{item.name}}</a>
                                </el-menu-item>
                            </el-menu-item-group>
                        </el-submenu>

                        <el-menu-item
                            index="6"
                            :class="{'is-active': switchContent == 'investment-planner'}"
                        >
                            <a
                                class="aside-link"
                                @click="changeUrl('investment-planner');handleOpen()"
                            >
                                <i class="icon-account icon-planner"></i>
                                <span slot="title">{{$t('profile.planner')}}</span>
                            </a>
                        </el-menu-item>

                        <el-menu-item index="7" :class="{'is-active': switchContent == 'profile'}">
                            <a class="aside-link" @click="changeUrl('profile');handleOpen()">
                                <i class="icon-account icon-profile"></i>
                                <span slot="title">{{$t('profile.profile')}}</span>
                            </a>
                        </el-menu-item>
                    </el-menu>
                </el-aside>
                <el-main
                    class="main-container"
                    id="main-container"
                    :class="{investPlanner:switchContent=='investment-planner'}"
                >
                    <transition>
                        <keep-alive :max="20">
                            <profileContainer
                                @getUserInfo="getUserInfo"
                                @loaded="loaded"
                                :nationalities="nationalities"
                                :userProfile="userProfile"
                                v-if="switchContent == 'profile' && userProfile.id != ''"
                            />

                            <PIDContainer
                                :requestParams="requestParams"
                                :requestHeaders="requestHeaders"
                                :propertySelector="propertySelector"
                                :browser_currency_symbol="browser_currency_symbol"
                                :portfolioResp="portfolioResp"
                                @loaded="loaded"
                                @editProperty="editProperty"
                                @goToManage="linkToManage"
                                @closeAside="closeAside"
                                v-if="switchContent == 'properties'"
                            />

                            <portfolioContainer
                                :requestParams="requestParams"
                                :requestHeaders="requestHeaders"
                                :portfolioResp="portfolioResp"
                                @loaded="loaded"
                                @addProperty="addProperty"
                                @linkToPID="linkToPID"
                                v-if="switchContent == 'portfolio'"
                            />
                            <addPropContainer
                                :requestParams="requestParams"
                                :requestHeaders="requestHeaders"
                                :portfolioResp="portfolioResp"
                                :nationalities="nationalities"
                                @loaded="loaded"
                                @goToPortfolio="viewPortfolio"
                                @updateAside="updateAside"
                                v-if="switchContent == 'addProperty'"
                            />
                            <manageContainer
                                :requestParams="requestParams"
                                :requestHeaders="requestHeaders"
                                :propertySelector="propertySelector"
                                :portfolioResp="portfolioResp"
                                :browser_currency_symbol="browser_currency_symbol"
                                @linkToPID="linkToPID"
                                @loaded="loaded"
                                v-if="switchContent == 'manage'"
                            />
                            <investPlannerContainer
                                :requestParams="requestParams"
                                :requestHeaders="requestHeaders"
                                :propertySelector="propertySelector"
                                :browser_currency_symbol="browser_currency_symbol"
                                @linkToPID="linkToPID"
                                @addProperty="addProperty"
                                @loaded="loaded"
                                v-if="switchContent == 'investment-planner'"
                            />
                            <editPropContainer
                                :requestParams="requestParams"
                                :requestHeaders="requestHeaders"
                                :portfolioResp="portfolioResp"
                                @updateAside="updateAside"
                                @linkToPID="linkToPID"
                                @loaded="loaded"
                                :switchContent="switchContent"
                                :property_info="property_info"
                                v-if="switchContent == 'editMortgage' || switchContent == 'editRent' || switchContent == 'editExpense' || switchContent == 'editLayout'"
                            />
                        </keep-alive>
                    </transition>
                </el-main>
            </el-container>
        </b-container>
    </div>
</template>

<script>
let loading;
import profileContainer from "@/components/account/profileContainer.vue";
import portfolioContainer from "@/components/account/portfolioContainer.vue";
import PIDContainer from "@/components/account/PIDContainer.vue";
import addPropContainer from "@/components/account/addPropContainer.vue";
import editPropContainer from "@/components/account/editPropContainer.vue";
import manageContainer from "@/components/account/manageContainer.vue";
import investPlannerContainer from "@/components/account/investPlannerContainer.vue";

let defaultAvatar = require("../../../../website_20/static/img/icon-account-aside-defaultUserAvatar.svg");

export default {
    name: "accountContainer",
    data() {
        return {
            browser_currency_symbol: "",
            switchContent: "",
            urlAmount: [],

            propertyId: 0,
            propertySelector: {},

            requestParams: {
                userId: this.$root.userId,
                token: this.$root.token,
                ppbApi: this.$root.ppbApi
            },

            portfolioResp: {},
            property_info: {},

            user: {
                name: "",
                tier: "",
                defaultAvatar: defaultAvatar
            },

            userProfile: {
                id: "",
                customer_level: "",
                birth: "",
                first_name: "",
                last_name: "",
                phoneNumber: "",
                email: "",
                nationality: "",
                nationalityName: "",
                cityResidence: "",
                username: "",
                gender: "",
                currency: "",
                currency_code: "",
                currency_symbol: "",
                notification: "",
                picture: "",
                language: "",
                status: "",
                has_notification: "",
                is_staff: "",
                areaUnit: "",
                forced_areaUnit: "",
                last_sync: "",
                profile_completed: "",
                property_purpose_completed: "",
                retirement_completed: ""
            },
            nationalities: [],

            isCollapse: false,
            activePane: []
        };
    },

    beforeMount() {
        this.switchContent = this.$route.name;

        this.urlAmount = ["profile", "portfolio", "properties"];

        this.loading();
        this.initialData();
    },
    mounted() {},

    methods: {
        closeAside(data) {
            if (data == "prop") {
                this.activePane = ["5"];
            } else {
                this.activePane = ["4"];
            }
        },

        loading() {
            loading = this.$loading({
                lock: true,
                target: ".main-container",
                text: this.$t("searchResultPage.loading"),
                color: "#3b6bbf",
                background: "rgba(255, 255, 255, 0.8)"
            });
        },

        initialData() {
            this.getUser = `${this.requestParams.ppbApi}api/user/${this.requestParams.userId}/`;
            this.useCode = `${this.requestParams.ppbApi}api/vipcode/upgrade/`;
            this.reqUrl = `${this.requestParams.ppbApi}api/user/${this.requestParams.userId}/settings/`;
            this.getNationalitiesUrl = `${this.requestParams.ppbApi}api/geo/countries/`;
            this.getPropUrl = `${this.requestParams.ppbApi}api/passbook/property/`;

            this.requestHeaders = {
                headers: {
                    Authorization: `Token ${this.requestParams.token}`
                }
            };

            let requestHeaders = this.requestHeaders;
            if (this.$root.cookie.acreage != "en") {
                requestHeaders.headers["Accept-Language"] = "zh-Hans";
                this.requestHeaders.headers["Accept-Language"] = "zh-Hans";
            }
            this.$http
                .get(this.getNationalitiesUrl, requestHeaders)
                .then(resp => {
                    this.nationalities = resp.data;
                });

            // this.$http.get(this.getPropUrl, this.requestHeaders).then(resp => {
            //     this.getPropManageModel = resp.data;
            // });

            this.$http.get(this.getUser, requestHeaders).then(rep => {
                this.userProfile = rep.data;

                this.browser_currency_symbol = rep.data.currency_symbol;

                this.user = {
                    name: `${rep.data.first_name || ""} ${rep.data.last_name ||
                        ""}`,
                    tier: rep.data.customer_level.toLowerCase(),
                    tierLabel: this.$t(
                        "profile.customer_level." + rep.data.customer_level
                    ),
                    defaultAvatar: rep.data.picture || defaultAvatar
                };
            });

            let url = `${this.requestParams.ppbApi}api/v2/portfolio/${this.requestParams.userId}`;

            this.$http.get(url, this.requestHeaders).then(resp => {
                if (resp.status == 200) {
                    this.portfolioResp = resp.data;
                    if (resp.data.properties.length > 0) {
                        this.propertyId = resp.data.properties[0].id;
                        this.propertySelector = resp.data.properties[0];
                    }
                    loading.close();
                }
            });
        },

        changeUrl(target) {
            this.$router.replace(`/${target}`);
            this.switchContent = target;
            $("a[data-lang='en']").attr("href", `/${target}`);
            $("a[data-lang='zh']").attr("href", `/zh/${target}`);
        },

        addProperty(data) {
            this.switchContent = "addProperty";
        },

        editProperty(tar) {
            if (tar.editTar == "layout") {
                this.switchContent = "editLayout";
            } else if (tar.editTar == "mortgage") {
                this.switchContent = "editMortgage";
            } else if (tar.editTar == "expense") {
                this.switchContent = "editExpense";
            } else if (tar.editTar == "rent") {
                this.switchContent = "editRent";
            }
            this.property_info = tar.currentRoute;
        },

        getUserInfo(data) {
            this.user = {
                name: `${data.first_name || ""} ${data.last_name || ""}`,
                tier: data.customer_level.toLowerCase(),
                tierLabel: this.$t(
                    "profile.customer_level." + data.customer_level
                ),
                defaultAvatar: data.picture || defaultAvatar
            };
        },

        linkToPID(item) {
            if (this.switchContent != "properties") {
                this.switchContent = "properties";
            }
            this.propertySelector = item;
            this.changeUrl("properties");
        },

        linkToManage(item) {
            if (this.switchContent != "manage") {
                this.switchContent = "manage";
            }
            this.propertySelector = item;
            this.changeUrl("manage");
        },

        viewPortfolio(tar) {
            this.changeUrl(tar);
        },

        updateAside(data) {
            let url = `${this.requestParams.ppbApi}api/v2/portfolio/${this.requestParams.userId}`;

            this.$http.get(url, this.requestHeaders).then(resp => {
                if (resp.status == 200) {
                    this.portfolioResp = resp.data;
                    if (resp.data.properties.length > 0) {
                        this.propertyId = resp.data.properties[0].id;
                        this.propertySelector = resp.data.properties[0];
                    }
                    loading.close();
                }
            });
        },
        collapseSide() {
            this.isCollapse = !this.isCollapse;
        },

        loaded(data) {
            if (data) {
                setTimeout(() => {
                    $("html, body").animate(
                        {
                            scrollTop: $("#main-container").offset().top + 20 // 90 is target height
                        },
                        700
                    );
                }, 400);
            }
        },

        handleOpen() {
            if (window.innerWidth > 414) return;
            $(".el-submenu.is-active.is-opened").remove(".is-opened");

            let promiseCallback = new Promise((resolve, reject) => {
                resolve(this.loaded());
            });

            promiseCallback.then(() => {
                setTimeout(() => {
                    $("html, body").animate(
                        {
                            scrollTop: $("#main-container").offset().top + 20 // 90 is target height
                        },
                        700
                    );
                }, 400);
            });
        }
    },

    components: {
        profileContainer,
        PIDContainer,
        portfolioContainer,
        addPropContainer,
        editPropContainer,
        manageContainer,
        investPlannerContainer
    },

    watch: {
        $route: {
            handler(newVal, oldVal) {
                if (this.urlAmount.includes(newVal.name)) {
                    this.switchContent = newVal.name;
                }
            },
            deep: true
        }
    }
};
</script>

<style lang="scss">
.el-form-item.is-error .el-input__inner,
.el-form-item.is-error .el-input__inner:focus,
.el-form-item.is-error .el-textarea__inner,
.el-form-item.is-error .el-textarea__inner:focus,
.el-message-box__input input.invalid,
.el-message-box__input input.invalid:focus {
    border-color: #c00109;
}

.el-form-item__error {
    color: #c00109;
}

.el-container .main-container h2 {
    height: 30px;
    font-family: GothamBook;
    font-size: 24px;
    line-height: 32px;
    color: #2e384d;
}
.account-page {
    padding: 28px 16px;
    background-color: #f6f9fc;
    .account-container {
        padding: 0;
        background-color: #fff;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 10px 0 rgba(155, 155, 155, 0.5);
        .el-container {
            flex-wrap: wrap;
            .main-container {
                padding: 25px 16px;
                &.investPlanner {
                    padding: 0;
                }
                .form-container {
                    margin: 25px 0;
                }
                .profile-container {
                    border-top: 1px solid #ebebeb;
                    padding-top: 30px;
                }
            }
        }
        .el-menu {
            border-right: 0;
            .el-menu-item {
                &.is-active {
                    padding-left: 20px;
                    background-color: rgba(46, 91, 255, 0.1);
                    a {
                        color: var(--windows-blue);
                    }
                }
            }
            .user-avatar {
                display: flex;
                align-content: center;
                flex-wrap: wrap;
                .user-img {
                    position: relative;
                    line-height: normal;
                    &:after {
                        content: "";
                        position: absolute;
                        bottom: -6px;
                        right: -6px;
                        width: 18px;
                        height: 18px;
                        display: inline-block;
                        background-size: cover;
                        background-repeat: no-repeat;
                    }
                    img {
                        width: 32px;
                        height: 32px;
                        border-radius: 6px;
                        object-fit: cover;
                    }
                }
                .user-info,
                .user-name {
                    display: block;
                }
                .user-info {
                    line-height: normal;
                    margin-left: 10px;
                    flex: 1;
                    width: 0;
                }
                .user-name {
                    width: 100%;
                    display: block;
                    line-height: normal;
                    font-family: GothamBook;
                    font-size: 14px;
                    color: #2e384d;
                    margin-bottom: 4px;
                    text-overflow: ellipsis;
                    overflow: hidden;
                }
                .user-class {
                    font-family: GothamBook;
                    font-size: 10px;
                    height: 15px;
                    line-height: 15px;
                }

                &.web_trial {
                    .user-img {
                        &:after {
                            background-image: url("../../../../website_20/static/img/icon-user-free.svg");
                        }
                    }
                    .user-class {
                        color: #447ef6;
                    }
                }
                &.classic {
                    .user-img {
                        &:after {
                            background-image: url("../../../../website_20/static/img/icon-user-classic.svg");
                        }
                    }
                    .user-class {
                        color: #bc3036;
                    }
                }
                &.premier {
                    .user-img {
                        &:after {
                            background-image: url("../../../../website_20/static/img/icon-user-premier.svg");
                        }
                    }
                    .user-class {
                        color: #203862;
                    }
                }
            }

            .aside-link {
                display: inline-block;
                width: 100%;
                height: 100%;
                font-family: GothamMedium;
                font-size: 14px;
                font-weight: 500;
                color: var(--brownish-grey);
            }
            .icon-account-toggle,
            .icon-account {
                position: relative;
                top: 0;
                left: 0;
                width: 20px;
                height: 21px;
                margin-right: 20px;
                display: inline-block;
            }
            .icon-account-toggle {
                width: 18px;
                height: 14px;
                background: url("../../../../website_20/static/img/icon-account-toggle.svg")
                    no-repeat;
                background-size: cover;
            }
            .icon-profile {
                background: url("../../../../website_20/static/img/icon-account-profile.svg")
                    no-repeat;
                background-size: cover;
            }
            .icon-manage {
                background: url("../../../../website_20/static/img/icon-account-manage.svg")
                    no-repeat;
                background-size: cover;
            }
            .icon-planner {
                background: url("../../../../website_20/static/img/icon-account-planner.svg")
                    no-repeat;
                background-size: cover;
            }
            .icon-addProp {
                width: 12px;
                height: 12px;
                margin-right: 10px;
                background: url("../../../../website_20/static/img/addProp/icon-add-property-blue.svg")
                    no-repeat;
                background-size: cover;
            }
            .icon-portfolio {
                background: url("../../../../website_20/static/img/icon-account-portfolio.svg")
                    no-repeat;
                background-size: cover;
            }
            .icon-property {
                background: url("../../../../website_20/static/img/icon-account-property.svg")
                    no-repeat;
                background-size: cover;
            }
        }
    }
    .el-aside {
        &,
        .profile-aside-menu.el-menu {
            max-width: unset;
            overflow: hidden;
            width: 100%;
        }
        .el-menu-item:first-child {
            display: none;
        }
        .user-benefits {
            width: 100%;
        }
        .el-menu-item.is-active {
            position: relative;
            &:after {
                content: "";
                position: absolute;
                width: 15px;
                height: 13px;
                right: 20px;
                top: 23px;
                background: url("../../../../website_20/static/img/icon-select.svg")
                    no-repeat;
            }
            &:nth-child(1),
            &:nth-child(2) {
                &:after {
                    content: unset;
                }
            }
        }
    }
    .el-menu {
        .el-menu-item.is-active {
            border-left: 3px solid #447ef6;
        }
        .el-menu-item:first-child {
            height: auto;
            &:hover,
            &:focus,
            &.is-active {
                background-color: unset !important;
                border-left: unset;
            }
        }
        & > .el-menu-item:nth-child(2) {
            padding: 30px 0 10px 20px;
            height: auto;
            &:hover,
            &:focus,
            &.is-active {
                background-color: unset !important;
                border-left: unset;
            }
        }
    }
}

.user-img {
    position: relative;
    line-height: normal;
    &:after {
        content: "";
        position: absolute;
        bottom: -6px;
        right: -6px;
        width: 18px;
        height: 18px;
        display: inline-block;
        background-size: cover;
        background-repeat: no-repeat;
    }
    img {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        object-fit: cover;
    }
}

.user-class {
    font-family: GothamBook;
    font-size: 10px;
    height: 15px;
    line-height: 15px;
}

.user-benefits {
    display: block;
    height: 23px;
    line-height: 23px;
    font-family: GothamBook;
    font-size: 10px;
    color: var(--windows-blue);
    &:hover {
        color: var(--windows-blue);
    }
}

.profile-aside-menu:not(.el-menu--collapse) {
    width: 200px;
    // because we hide other links for now
    // min-height: 400px;
}

.el-aside {
    color: #333;
}

@media (min-width: 768px) {
    .account-page {
        .account-container .el-container {
            flex-wrap: nowrap;
            .main-container {
                padding: 28px;
                &.investPlanner {
                    padding: 0;
                }
                border-left: 1px solid #e6e6e6;
                .profile-container {
                    width: 100%;
                    padding-top: 0;
                    border-top: 0;
                }
            }
        }
        .el-aside {
            max-width: 215px;
            width: auto;
            .profile-aside-menu.el-menu {
                width: 215px;
                max-width: 215px;
                &.el-menu--collapse {
                    width: 64px;
                }
            }
            .el-menu-item:first-child {
                display: block;
            }
            .el-menu-item.is-active:after {
                content: unset;
            }
            .el-menu-item.property-list {
                a {
                    font-family: GothamBook;
                    font-size: 14px;
                    color: var(--brownish-grey);
                    width: 100%;
                    height: 100%;
                    display: block;
                }
                &:hover {
                    outline: 0;
                    background-color: #ecf5ff !important;
                }
                &.is-active {
                    border-left: 3px solid #447ef6;
                    background-color: rgba(46, 91, 255, 0.1);
                }
            }
        }
    }

    .account-page {
        padding: 50px 0;
    }
    // .account-container.container {
    //     width: 712px;
    // }
}

@media (min-width: 992px) {
}

@media (min-width: 1200px) {
    .account-container.container {
        width: 1100px;
    }
}
</style>


// WEBPACK FOOTER //
// src/components/account/accountContainer.vue